// MACHINERY V2 (Stroje) — SR create page, and SR EDIT page (M2.6).
//
// Route: `/machinery-v2/:id/service-reports/new`. Spec: data package §7 (Create)
// + §8 (Edit). Reference impl: design_handoff_machinery_v3/machinery-sr.jsx
// `SrCreateScreen`; artboards 0702 / 0703.
//
// >>> THE MACHINE CARD IS ON THE LEFT. <<<
// `machineSide="left"` is the designer's explicit change from v1, where the
// machine block sits under the form (ticket §5 #7). Desktop grid is
// `280px minmax(0, 1fr)`; the card drops above the form on phone.
//
// >>> EDIT REUSES THIS PAGE. THERE IS NO EDIT URL. <<<
// `isEdit` comes from `customerServiceReports.serviceReport` being populated
// (§8) — the same shared slot every SR entry point in v2 goes through
// srNavigation.ts to set or clear. Header, breadcrumb tail and submit label each
// branch on it with their own i18n keys (`srPage.newReportTitle` vs
// `editReportTitle`; `createServiceReport` vs `editServiceReport`;
// `srPage.createReport` vs `srPage.saveChanges`), exactly as §7 lists them.
//
// THE PAGE LOADER GATE IS v1's: a full-page notice while `!machine && machineId`
// (CreateServiceReportPage.tsx:386-388). A pending `getMachineParts` does NOT
// block — the form renders and the part list fills in (§7 "States").
//
// BUT THE GATE HAS A WAY OUT (M2.6 review C7). v1's notice is terminal: a
// rejected `getMachine` (network, 404, permission) leaves it saying "Loading…"
// for ever, with no message and no action, because the early return sits ABOVE
// the tree that mounts MachineryToastHost. The fetch is therefore tracked per
// machine id (useSrMachineFetch.ts, split out for the 300-line cap), so the
// gate can offer RetryNotice, distinguish a settled-but-empty fetch
// ("not found") from a failure, and mount the toast host on BOTH paths.
// `getMachine.rejected` writes `customerMachinery.error`
// (customerMachinerySlice.ts:199-202); that host is what turns it into
// something the user can read.
//
// This file owns the ROUTE + CHROME + LAYOUT. The form data and behaviour are
// useSrCreateForm.ts; the fields are SrCreateFields.tsx; the machine fetch and
// its per-id outcome are useSrMachineFetch.ts (300-line cap).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { getMachineParts } from "../../customerMachineServiceReportsSlice";
import { Notice } from "../fleet/FleetNotices";
import MachineryToastHost from "../feedback/MachineryToastHost";
import RetryNotice from "../feedback/RetryNotice";
import { CheckGlyph } from "../forms/FormGlyphs";
import SrCreateFields from "./SrCreateFields";
import SrMachineCard from "./SrMachineCard";
import SrPageBreadcrumb from "./SrPageBreadcrumb";
import { buildPartOptions } from "./srMachineParts";
import { MACHINERY_V2_BASE } from "./srNavigation";
import { useSrCreateForm } from "./useSrCreateForm";
import { useSrMachineFetch } from "./useSrMachineFetch";

const SrCreateV2: React.FC = () => {
  const { id } = useParams();
  const machineId = id || "";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const { isPhone } = useBreakpoint();

  const machineParts = useAppSelector((s) => s.customerServiceReports.machineParts);

  const { form, isEdit, files, fileError, addFiles, removeFile, submitting, submit } =
    useSrCreateForm(machineId);

  /** The machine, its per-id outcome and the retry — see useSrMachineFetch.ts. */
  const { current, settled, failed, reload } = useSrMachineFetch(machineId);

  useEffect(() => {
    dispatch(getMachineParts());
  }, [dispatch]);

  const partOptions = useMemo(
    () => buildPartOptions(machineParts, translate),
    [machineParts, translate]
  );

  const profilePath = `${MACHINERY_V2_BASE}/${machineId}`;

  if (!current && machineId) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
        <main style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 24px" }}>
          {failed ? (
            <RetryNotice
              title={translate("myMachinery:v2.sr.machineLoadFailed")}
              hint={translate("myMachinery:v2.tabs.loadFailedHint")}
              onRetry={reload}
              busy={!settled}
            />
          ) : settled ? (
            <Notice text={translate("myMachinery:machineNotFound")} />
          ) : (
            <Notice text={translate("myMachinery:v2.profile.loading")} dashed={false} />
          )}
        </main>
        {/* C7: the host must be reachable on the FAILURE path too. This early
            return is above the tree that mounts the one at the bottom of the
            file, so without this the slice's own error toast could never
            appear while the gate was up. */}
        <MachineryToastHost />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
      <SrPageBreadcrumb
        machineId={machineId}
        machineName={current ? getMachineDisplayName(current) : ""}
        current={translate(
          isEdit ? "myMachinery:editServiceReport" : "myMachinery:createServiceReport"
        )}
      />

      {/* Title row — artboard 0702: back button, then the H1. */}
      <div
        style={{
          padding: "16px 24px 4px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          minWidth: 0,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(profilePath)}
          aria-label={translate("myMachinery:srPage.backToMachine")}
          style={{
            width: 32,
            height: 32,
            padding: 0,
            flexShrink: 0,
            border: "1px solid var(--border)",
            background: "var(--card)",
            color: "var(--ink-2)",
            cursor: "pointer",
            borderRadius: 8,
            display: "grid",
            placeItems: "center",
          }}
        >
          <svg
            viewBox="0 0 12 12"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden
          >
            <path d="M7.5 2 L3 6 L7.5 10" />
          </svg>
        </button>
        <h1
          style={{
            margin: 0,
            fontSize: isPhone ? 20 : 26,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--ink)",
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {translate(
            isEdit ? "myMachinery:srPage.editReportTitle" : "myMachinery:srPage.newReportTitle"
          )}
        </h1>
      </div>

      <main
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          padding: isPhone ? "12px 14px 24px" : "16px 24px 28px",
        }}
      >
        <form onSubmit={submit} style={{ maxWidth: 1240, minWidth: 0 }}>
          <div
            style={{
              display: "grid",
              // Machine card LEFT (§5 #7). Phone stacks it above the form.
              gridTemplateColumns: isPhone ? "1fr" : "280px minmax(0, 1fr)",
              gap: isPhone ? 14 : 20,
              alignItems: "start",
              minWidth: 0,
            }}
          >
            {current && (
              <SrMachineCard machine={current} onOpenProfile={() => navigate(profilePath)} />
            )}

            <section
              style={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                boxShadow: "var(--shadow)",
                padding: isPhone ? "14px 14px 2px" : "16px 18px 2px",
                minWidth: 0,
              }}
            >
              <SrCreateFields
                form={form}
                partOptions={partOptions}
                files={files}
                fileError={fileError}
                onAddFiles={addFiles}
                onRemoveFile={removeFile}
              />
            </section>
          </div>

          {/* Footer actions — artboard 0702, right-aligned under the grid.
              Sticky on phone (§15) so the primary action stays reachable. */}
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: "1px solid var(--border-soft)",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 10,
              maxWidth: 1240,
              position: isPhone ? "sticky" : "static",
              bottom: 0,
              background: isPhone ? "var(--bg)" : "transparent",
              paddingBottom: isPhone ? 10 : 0,
              flexWrap: "wrap",
            }}
          >
            <button type="button" className="btn" onClick={() => navigate(profilePath)}>
              {translate("myMachinery:srPage.cancel")}
            </button>
            <button type="submit" className="btn btn-mod" disabled={submitting}>
              <CheckGlyph />
              {translate(
                isEdit ? "myMachinery:srPage.saveChanges" : "myMachinery:srPage.createReport"
              )}
            </button>
          </div>
        </form>
      </main>

      {/* The sanctioned v2 feedback primitive — mounted so a toast raised by a
          neighbouring surface (e.g. the slice's own error) still renders here. */}
      <MachineryToastHost />
    </div>
  );
};

export default SrCreateV2;
